import { Link } from "react-router-dom";
import { Mail } from "lucide-react";
import FadeIn from "@/components/FadeIn";
import SeoHead from "@/components/SeoHead";

export default function AlgemeneVoorwaarden() {
  return (
    <div data-testid="page-algemene-voorwaarden" className="pt-[96px]">
      <SeoHead
        title="Algemene voorwaarden — SovEU"
        description="Lees de algemene voorwaarden van SovEU."
        path="/algemene-voorwaarden"
      />

      {/* Hero */}
      <section className="section bg-white">
        <div className="container-soveu max-w-[860px]">
          <FadeIn>
            <div className="eyebrow mb-4">Juridisch</div>
            <h1 className="h1-display mb-5">Algemene voorwaarden SovEU</h1>
            <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-[14px] text-[#6B7280]">
              <div><span className="font-medium text-[#1E3A5F]">Versie:</span> mei 2026</div>
              <div><span className="font-medium text-[#1E3A5F]">Organisatie:</span> SovEU</div>
              <div><span className="font-medium text-[#1E3A5F]">KVK:</span> 42033105</div>
              <div>
                <span className="font-medium text-[#1E3A5F]">E-mail:</span>{" "}
                <a href="mailto:info@soveu.nl" className="underline">info@soveu.nl</a>
              </div>
            </dl>
          </FadeIn>
        </div>
      </section>

      {/* Body */}
      <article className="pb-16">
        <div className="container-soveu max-w-[860px] prose-soveu">
          <FadeIn>
            <Article number="1" title="Definities">
              <ol>
                <li><strong>SovEU:</strong> de eenmanszaak SovEU, gevestigd te Utrecht, ingeschreven bij de Kamer van Koophandel onder nummer 42033105.</li>
                <li><strong>Opdrachtgever:</strong> de natuurlijke of rechtspersoon die met SovEU een Overeenkomst heeft gesloten of wenst te sluiten.</li>
                <li><strong>Overeenkomst:</strong> elke afspraak tussen SovEU en Opdrachtgever tot het verlenen van diensten door SovEU ten behoeve van Opdrachtgever.</li>
              </ol>
            </Article>

            <Article number="2" title="Toepasselijkheid">
              <ol>
                <li>Deze algemene voorwaarden zijn van toepassing op alle aanbiedingen, offertes, werkzaamheden, en Overeenkomsten tussen SovEU en Opdrachtgever.</li>
                <li>Afwijkingen van deze voorwaarden zijn slechts geldig indien deze uitdrukkelijk en schriftelijk zijn overeengekomen.</li>
                <li>De toepasselijkheid van eventuele inkoop- of andere voorwaarden van Opdrachtgever wordt uitdrukkelijk van de hand gewezen.</li>
              </ol>
            </Article>

            <Article number="3" title="Offertes en totstandkoming overeenkomst">
              <ol>
                <li>Alle offertes van SovEU zijn vrijblijvend en hebben een geldigheidsduur van 30 dagen, tenzij anders aangegeven.</li>
                <li>Een Overeenkomst komt tot stand op het moment dat Opdrachtgever de offerte van SovEU schriftelijk (inclusief per e-mail) akkoord bevindt, dan wel wanneer SovEU met instemming van Opdrachtgever feitelijk een aanvang heeft gemaakt met de uitvoering van de werkzaamheden.</li>
              </ol>
            </Article>

            <Article number="4" title="Uitvoering van de overeenkomst (scope en inspanningsverplichting)">
              <ol>
                <li>SovEU voert de werkzaamheden uit naar beste inzicht en vermogen en overeenkomstig de eisen van goed vakmanschap. Het betreft te allen tijde een inspanningsverplichting en uitdrukkelijk geen resultaatsverplichting.</li>
                <li>
                  <strong>Uitsluitingen:</strong> de dienstverlening van SovEU is bestuurlijk en adviserend van aard. Tenzij expliciet anders overeengekomen, omvat de dienstverlening uitdrukkelijk géén:
                  <ul>
                    <li>technische implementaties, migraties of configuraties van IT-systemen;</li>
                    <li>technische cybersecurity-audits, penetratietesten (pentests) of forensisch onderzoek;</li>
                    <li>juridisch advies (zoals het opstellen of beoordelen van individuele verwerkersovereenkomsten);</li>
                    <li>afgifte van formele compliance-certificeringen, accountantsverklaringen of CO2-audits.</li>
                  </ul>
                </li>
                <li>Rapporten en analyses (zoals de Bestuursscan of het Regiebeeld) bieden een onafhankelijke bestuurlijke risicofoto op basis van de door de Opdrachtgever verstrekte informatie en publiek beschikbare bronnen. De besluitvorming en de gevolgen daarvan blijven te allen tijde de volledige verantwoordelijkheid van de Opdrachtgever.</li>
              </ol>
            </Article>

            <Article number="5" title="Medewerking door de opdrachtgever">
              <ol>
                <li>Opdrachtgever draagt er zorg voor dat alle gegevens, documenten en toegang tot systemen of personen, waarvan SovEU aangeeft dat deze noodzakelijk zijn of waarvan Opdrachtgever redelijkerwijs behoort te begrijpen dat deze noodzakelijk zijn voor het uitvoeren van de Overeenkomst, tijdig worden verstrekt.</li>
                <li>Indien de benodigde gegevens niet tijdig of niet volledig zijn verstrekt, heeft SovEU het recht de uitvoering van de Overeenkomst op te schorten en/of de uit de vertraging voortvloeiende extra kosten in rekening te brengen.</li>
              </ol>
            </Article>

            <Article number="6" title="Tarieven en betaling">
              <ol>
                <li>Alle door SovEU vermelde prijzen en tarieven zijn exclusief BTW en andere heffingen van overheidswege.</li>
                <li>Facturering geschiedt op basis van de in de offerte afgesproken fasering (bijvoorbeeld 100% achteraf voor een Bestuursscan, of een gefaseerde betaling bij langere trajecten).</li>
                <li>Betaling dient te geschieden binnen 30 dagen na factuurdatum, tenzij schriftelijk anders is overeengekomen.</li>
                <li>Bij overschrijding van de betalingstermijn is Opdrachtgever van rechtswege in verzuim en is de wettelijke handelsrente verschuldigd.</li>
              </ol>
            </Article>

            <Article number="7" title="Geheimhouding (vertrouwelijkheid)">
              <ol>
                <li>Beide partijen zijn verplicht tot strikte geheimhouding van alle vertrouwelijke informatie die zij in het kader van hun Overeenkomst van elkaar of uit andere bron hebben verkregen.</li>
                <li>Rapporten en adviezen geleverd door SovEU zijn uitsluitend bestemd voor intern gebruik door de Opdrachtgever en mogen niet zonder voorafgaande schriftelijke toestemming van SovEU aan derden worden verstrekt of openbaar worden gemaakt, behoudens wettelijke verplichtingen.</li>
              </ol>
            </Article>

            <Article number="8" title="Aansprakelijkheid">
              <ol>
                <li>SovEU is uitsluitend aansprakelijk voor directe schade die het rechtstreekse gevolg is van een toerekenbare tekortkoming in de uitvoering van de opdracht.</li>
                <li>De totale aansprakelijkheid van SovEU wegens toerekenbare tekortkoming is te allen tijde beperkt tot maximaal het factuurbedrag dat is gefactureerd voor de specifieke opdracht waaruit de schade is voortgevloeid, met een maximum van € 25.000. Voor doorlopende overeenkomsten is de aansprakelijkheid beperkt tot het bedrag dat gedurende de laatste drie maanden voorafgaand aan de schadeveroorzakende gebeurtenis in rekening is gebracht.</li>
                <li>Aansprakelijkheid voor indirecte schade, daaronder begrepen gevolgschade, gederfde winst, gemiste besparingen, reputatieschade, boetes van toezichthouders (zoals de Autoriteit Persoonsgegevens) en schade door bedrijfsstagnatie, is in alle gevallen uitdrukkelijk uitgesloten.</li>
                <li>SovEU is niet aansprakelijk voor schade van welke aard ook, doordat SovEU is uitgegaan van door de Opdrachtgever verstrekte onjuiste en/of onvolledige informatie.</li>
              </ol>
            </Article>

            <Article number="9" title="Intellectueel eigendom">
              <ol>
                <li>SovEU behoudt zich alle rechten van intellectueel eigendom voor met betrekking tot producten van de geest welke zij gebruikt of heeft gebruikt (zoals de SovEU Regiemethode, scoringsmodellen, templates en raamwerken).</li>
                <li>De Opdrachtgever verkrijgt uitsluitend een niet-exclusief en niet-overdraagbaar gebruiksrecht op de definitieve rapporten en adviezen, specifiek voor de eigen organisatie.</li>
              </ol>
            </Article>

            <Article number="10" title="Toepasselijk recht en geschillen">
              <ol>
                <li>Op alle rechtsbetrekkingen waarbij SovEU partij is, is uitsluitend het Nederlands recht van toepassing.</li>
                <li>Geschillen zullen uitsluitend worden voorgelegd aan de bevoegde rechter in het arrondissement Midden-Nederland (locatie Utrecht).</li>
              </ol>
            </Article>
          </FadeIn>
        </div>
      </article>

      {/* CTA */}
      <section className="section" style={{ backgroundColor: "#F5F7FA" }} data-testid="voorwaarden-cta">
        <div className="container-soveu max-w-[860px]">
          <FadeIn>
            <div className="soveu-card p-8 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-5">
              <div>
                <h2 className="h3-display mb-2">Heeft u vragen over deze voorwaarden?</h2>
                <p className="text-[#334155] text-[15px]">
                  Neem contact op via <a href="mailto:info@soveu.nl" className="underline" style={{ color: "#1E3A5F" }}>info@soveu.nl</a>.
                </p>
              </div>
              <a
                href="mailto:info@soveu.nl"
                className="btn-primary"
                data-testid="voorwaarden-mail-cta"
              >
                <Mail size={16} />
                Mail SovEU
              </a>
            </div>
            <p className="text-[13px] text-[#6B7280] mt-6 text-center">
              Zie ook ons{" "}
              <Link to="/privacybeleid" className="underline" style={{ color: "#1E3A5F" }}>
                privacybeleid
              </Link>.
            </p>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}

function Article({ number, title, children }) {
  return (
    <section className="legal-section" data-testid={`voorwaarden-article-${number}`}>
      <h2>
        <span className="legal-num">Artikel {number}.</span> {title}
      </h2>
      {children}
    </section>
  );
}
